// Updated hook with GlowpickClient integration - v5.1 (with tab data persistence)
import React, { useState, useEffect, useCallback, useRef, startTransition } from 'react';
import type { GlowpickProduct, GlowpickPaginationInfo } from '../../../models/Glowpick';
import { glowpickClient } from '../../../infrastructure/GlowpickClient';
import { convertToLocalTime } from '../../../utils/dateUtils';

export type NavigationTab = 'newest' | 'awards' | 'brands' | 'category' | 'search';
export type RankingType = 'brand' | 'category';
export type AwardPeriod = '2025-1H' | '2024-2H' | '2024-1H' | '2023-2H' | '2023-1H' | '2022-2H' | '2022-1H';
export type SortOption = 'newest' | 'reviews' | 'rating' | 'price-high' | 'price-low' | 'ranking';

// Cache interface
interface CacheData {
  brands: string[];
  categories: string[];
  subcategories: Record<string, string[]>;
  lastFetched: {
    brands?: number;
    categories?: number;
  };
}

// Tab-specific data cache interface
interface TabDataCache {
  newest: GlowpickProduct[];
  awards: GlowpickProduct[];
  brands: GlowpickProduct[];
  category: GlowpickProduct[];
  search: GlowpickProduct[];
}

// Tab-specific loading state interface
interface TabLoadingState {
  newest: boolean;
  awards: boolean;
  brands: boolean;
  category: boolean;
  search: boolean;
}

// Generate award periods for 6 most recent periods
const generateAwardPeriods = (): AwardPeriod[] => {
  const now = new Date();
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth() + 1; // 1-12

  // Determine current period: 1~6 is 1H, 7~12 is 2H
  const latestYear = currentYear;
  const latestHalf: '1H' | '2H' = currentMonth <= 6 ? '1H' : '2H';

  const periods: AwardPeriod[] = [];
  let year = latestYear;
  let half = latestHalf;

  // Generate 4 recent periods going backwards
  for (let i = 0; i < 4; i++) {
    periods.push(`${year}-${half}` as AwardPeriod);

    // Move to previous period
    if (half === '1H') {
      half = '2H';
      year--;
    } else {
      half = '1H';
    }
  }

  return periods;
};

export const useGlowpick = () => {
  // Instance tracking for debugging
  const hookInstanceId = useRef(Math.floor(Math.random() * 1000));

  // Debounce tracking
  const lastFetchTime = useRef(0);
  const FETCH_DEBOUNCE_MS = 500; // Prevent fetches within 500ms of each other

  // Tab-specific data cache - separate products for each tab
  const [tabDataCache, setTabDataCache] = useState<TabDataCache>({
    newest: [],
    awards: [],
    brands: [],
    category: [],
    search: [],
  });

  // Tab-specific loading states
  const [tabLoadingState, setTabLoadingState] = useState<TabLoadingState>({
    newest: false,
    awards: false,
    brands: false,
    category: false,
    search: false,
  });

  const [lastUpdatedInfo, setLastUpdatedInfo] = useState<{ productId: string; date: string } | null>(null);

  // Add guard against multiple concurrent calls (React StrictMode protection)
  const fetchingLastUpdatedInfo = useRef(false);

  // Navigation state - start with search tab if URL has glowpickId to prevent bulk fetch
  const [activeTab, setActiveTab] = useState<NavigationTab>(() => {
    // Check if current URL has glowpickId parameter
    const currentPath = window.location.pathname;
    return currentPath.includes('/glowpickId/') ? 'search' : 'newest';
  });
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [selectedSubcategory, setSelectedSubcategory] = useState<string>('');
  const [selectedBrand, setSelectedBrand] = useState<string>('');
  const [selectedRankingType, setSelectedRankingType] = useState<RankingType>('brand');
  const [selectedAwardPeriod, setSelectedAwardPeriod] = useState<AwardPeriod>('2025-1H');
  const [sortBy, setSortBy] = useState<SortOption>('reviews');
  const [queryLimit, setQueryLimit] = useState<number>(() => {
    const savedLimit = localStorage.getItem('glowpick_query_limit');
    return savedLimit ? parseInt(savedLimit, 10) : 100;
  });

  // Pagination state (declared after queryLimit so we can use it)
  const [currentPage, setCurrentPage] = useState(1);
  const [paginationInfo, setPaginationInfo] = useState<GlowpickPaginationInfo>(() => ({
    currentPage: 1,
    totalPages: 1,
    totalProducts: 0,
    productsPerPage: queryLimit, // Use queryLimit for consistency
    hasNextPage: false,
    hasPreviousPage: false,
    startIndex: 0,
    endIndex: 0,
  }));

  // Cache state
  const [cache, setCache] = useState<CacheData>({
    brands: [],
    categories: [],
    subcategories: {},
    lastFetched: {},
  });

  // Computed property: get current tab's products
  const products = tabDataCache[activeTab];

  // Computed property: get current tab's loading state
  const loading = tabLoadingState[activeTab];

  // Store settings in localStorage whenever they change
  useEffect(() => {
    localStorage.setItem('glowpick_query_limit', queryLimit.toString());
  }, [queryLimit]);

  // Reset to page 1 when changing tabs or filters (but not for search tab)
  useEffect(() => {
    // Don't reset pagination for search tab - it has its own pagination logic
    if (activeTab === 'search') {
      return;
    }

    console.info(`🔄 [${hookInstanceId.current}] Resetting to page 1 due to filter/tab change`);
    setCurrentPage(1);
  }, [activeTab, selectedCategory, selectedSubcategory, selectedBrand, selectedAwardPeriod]);

  // Update pagination info when query limit changes
  useEffect(() => {
    setPaginationInfo((prev) => ({ ...prev, productsPerPage: queryLimit }));
  }, [queryLimit]);

  // Generated award periods
  const awardPeriods = React.useMemo(() => generateAwardPeriods(), []);

  // Helper function to calculate pagination info
  const calculatePaginationInfo = useCallback(
    (totalProducts: number, currentPage: number, pageSize: number): GlowpickPaginationInfo => {
      const totalPages = Math.ceil(totalProducts / pageSize);
      const startIndex = (currentPage - 1) * pageSize + 1;
      const endIndex = Math.min(currentPage * pageSize, totalProducts);

      return {
        currentPage,
        totalPages,
        totalProducts,
        productsPerPage: pageSize,
        hasNextPage: currentPage < totalPages,
        hasPreviousPage: currentPage > 1,
        startIndex,
        endIndex,
      };
    },
    [],
  );

  // Helper function to set loading state for specific tab
  const setTabLoading = useCallback((tab: NavigationTab, isLoading: boolean) => {
    setTabLoadingState((prev) => ({
      ...prev,
      [tab]: isLoading,
    }));
  }, []);

  // Helper function to set products for specific tab
  const setTabProducts = useCallback((tab: NavigationTab, products: GlowpickProduct[]) => {
    setTabDataCache((prev) => ({
      ...prev,
      [tab]: products,
    }));
  }, []);

  // lastEvaluatedKey calculation removed - using client-side pagination

  // API call functions using GlowpickClient
  const fetchBrands = useCallback(async () => {
    try {
      const brands = await glowpickClient.getAllBrands();

      setCache((prev) => ({
        ...prev,
        brands,
        lastFetched: { ...prev.lastFetched, brands: Date.now() },
      }));

      return brands;
    } catch (error) {
      console.error(`❌ [${hookInstanceId.current}] Error fetching brands:`, error);
      return [];
    }
  }, []);

  const fetchCategories = useCallback(async () => {
    try {
      const categoriesResponse = await glowpickClient.getAllCategories();
      const categories = categoriesResponse.categories || [];
      const subcategories = categoriesResponse.subcategories || {};

      setCache((prev) => ({
        ...prev,
        categories,
        subcategories,
        lastFetched: { ...prev.lastFetched, categories: Date.now() },
      }));

      return { categories, subcategories };
    } catch (error) {
      console.error(`❌ [${hookInstanceId.current}] Error fetching categories:`, error);
      return { categories: [], subcategories: {} };
    }
  }, []);

  // Utility function to calculate product ID range
  const calculateProductIdRange = useCallback((lastProductId: string): string => {
    const productIdNum = parseInt(lastProductId, 10);
    if (isNaN(productIdNum)) {
      console.warn('⚠️ Invalid lastProductId, using default range');
      return '1-1000'; // fallback range
    }

    // Calculate range based on 1000 increments
    const rangeStart = Math.floor(productIdNum / 1000) * 1000 + 1;
    const rangeEnd = rangeStart + 999;

    const range = `${rangeStart}-${rangeEnd}`;
    console.info(`📊 Calculated range for productId ${lastProductId}: ${range}`);
    return range;
  }, []);

  const fetchProducts = useCallback(
    async (page = 1, silent = false, targetTab?: NavigationTab): Promise<void> => {
      try {
        const tabToFetch = targetTab || activeTab;

        // Don't fetch products for search tab - it should remain empty
        if (tabToFetch === 'search') {
          console.info(`🔍 [${hookInstanceId.current}] Skipping fetchProducts for search tab`);
          return;
        }

        // Debounce to prevent rapid successive calls
        const now = Date.now();
        if (now - lastFetchTime.current < FETCH_DEBOUNCE_MS) {
          console.warn(`⚠️ [${hookInstanceId.current}] Debouncing fetchProducts call - too soon after last fetch`);
          return;
        }
        lastFetchTime.current = now;

        // Set loading state unless this is a silent refresh
        if (!silent) {
          setTabLoading(tabToFetch, true);
        }

        let searchResponse: any;

        // For now, fetch all products without pagination (client-side pagination)
        const fetchLimit = 1000;

        switch (tabToFetch) {
          case 'newest': {
            // New Arrival tab uses range-based query for latest products
            if (lastUpdatedInfo?.productId) {
              const range = calculateProductIdRange(lastUpdatedInfo.productId);
              console.info(`🆕 [${hookInstanceId.current}] Fetching New Arrival products with range: ${range}`);
              searchResponse = await glowpickClient.getProductsByRange(range, 100, undefined);
            } else {
              console.warn(`⚠️ [${hookInstanceId.current}] No lastProductId available, fetching without range`);
              searchResponse = await glowpickClient.getProducts(100, undefined); // Fallback to original behavior
            }
            break;
          }

          case 'awards': {
            // Awards tab only uses award period filter, ignoring category/brand filters
            if (selectedAwardPeriod) {
              searchResponse = await glowpickClient.getProductsByAward(selectedAwardPeriod, fetchLimit, undefined);
            } else {
              console.warn(`⚠️ [${hookInstanceId.current}] No award period selected for awards tab`);
              setTabProducts(tabToFetch, []);
              setTabLoading(tabToFetch, false);
              return;
            }
            break;
          }

          case 'brands': {
            // Brands tab: only fetch if a specific brand is selected
            if (selectedBrand) {
              searchResponse = await glowpickClient.getProductsByBrand(selectedBrand, fetchLimit, undefined);
            } else {
              // No brand selected - don't fetch anything, show empty state
              console.info(`🏷️ [${hookInstanceId.current}] No brand selected for brands tab - showing empty state`);
              setTabProducts(tabToFetch, []);
              setTabLoading(tabToFetch, false);
              return;
            }
            break;
          }

          case 'category': {
            // Category tab: only fetch if a specific category is selected
            if (selectedCategory) {
              searchResponse = await glowpickClient.getProductsByCategory(
                selectedCategory,
                selectedSubcategory,
                fetchLimit,
                undefined,
              );
            } else {
              // No category selected - don't fetch anything, show empty state
              console.info(
                `📂 [${hookInstanceId.current}] No category selected for category tab - showing empty state`,
              );
              setTabProducts(tabToFetch, []);
              setTabLoading(tabToFetch, false);
              return;
            }
            break;
          }

          default: {
            console.warn(`⚠️ [${hookInstanceId.current}] Unknown tab: ${tabToFetch}`);
            setTabProducts(tabToFetch, []);
            setTabLoading(tabToFetch, false);
            return;
          }
        }

        // Process the response
        if (searchResponse && searchResponse.products) {
          setTabProducts(tabToFetch, searchResponse.products);

          // Only update pagination info if this is for the currently active tab
          if (tabToFetch === activeTab) {
            const newPaginationInfo = calculatePaginationInfo(searchResponse.products.length, page, queryLimit);
            setPaginationInfo(newPaginationInfo);
            setCurrentPage(page);
          }
        } else {
          setTabProducts(tabToFetch, []);
          if (tabToFetch === activeTab) {
            setPaginationInfo(calculatePaginationInfo(0, 1, queryLimit));
            setCurrentPage(1);
          }
        }
      } catch (error) {
        console.error(`❌ [${hookInstanceId.current}] Error fetching products:`, error);
        const tabToFetch = targetTab || activeTab;
        setTabProducts(tabToFetch, []);
        if (tabToFetch === activeTab) {
          setPaginationInfo(calculatePaginationInfo(0, 1, queryLimit));
          setCurrentPage(1);
        }
      } finally {
        if (!silent) {
          const tabToFetch = targetTab || activeTab;
          setTabLoading(tabToFetch, false);
        }
      }
    },
    [
      activeTab,
      selectedCategory,
      selectedSubcategory,
      selectedBrand,
      selectedAwardPeriod,
      queryLimit,
      calculatePaginationInfo,
      setTabLoading,
      setTabProducts,
      lastUpdatedInfo,
      calculateProductIdRange,
    ],
  );

  const fetchLastUpdatedInfo = useCallback(async () => {
    // Prevent multiple concurrent calls (React StrictMode protection)
    if (fetchingLastUpdatedInfo.current) {
      console.info(`⏸️ [${hookInstanceId.current}] fetchLastUpdatedInfo already in progress, skipping`);
      return;
    }

    fetchingLastUpdatedInfo.current = true;
    try {
      console.info(`📡 [${hookInstanceId.current}] Fetching lastProductId...`);
      const response = await glowpickClient.getLastProductId();
      if (response) {
        const convertedDate = convertToLocalTime(response.lastUpdated);
        setLastUpdatedInfo({
          productId: response.lastProductId,
          date: convertedDate,
        });
        console.info(`✅ [${hookInstanceId.current}] lastProductId fetched: ${response.lastProductId}`);
      }
    } catch (error) {
      console.error(`❌ [${hookInstanceId.current}] Error fetching last updated info:`, error);
    } finally {
      fetchingLastUpdatedInfo.current = false;
    }
  }, []);

  // Filter and sort products based on current state
  const filteredAndSortedProducts = React.useMemo(() => {
    let filteredProducts = products;

    // Apply additional client-side filters for brands tab
    if (activeTab === 'brands' && selectedBrand) {
      filteredProducts = products.filter((product) => product.brand === selectedBrand);
    }

    // Sort products
    const sortedProducts = [...filteredProducts].sort((a, b) => {
      // For newest tab, always sort by productID (highest first) regardless of sortBy option
      if (activeTab === 'newest') {
        return parseInt(b.productId || '0') - parseInt(a.productId || '0');
      }

      switch (sortBy) {
        case 'newest':
          return new Date(b.createdAt || '').getTime() - new Date(a.createdAt || '').getTime();
        case 'reviews':
          return (b.reviews || 0) - (a.reviews || 0);
        case 'rating':
          return (b.rating || 0) - (a.rating || 0);
        case 'price-high':
          return (b.price || 0) - (a.price || 0);
        case 'price-low':
          return (a.price || 0) - (b.price || 0);
        case 'ranking':
          return (
            (a.rankings?.category?.ranking || a.rankings?.brand?.ranking || Number.MAX_SAFE_INTEGER) -
            (b.rankings?.category?.ranking || b.rankings?.brand?.ranking || Number.MAX_SAFE_INTEGER)
          );
        default:
          return 0;
      }
    });

    return sortedProducts;
  }, [products, activeTab, selectedBrand, sortBy]);

  // Get unique award categories for the current award period
  const awardCategories = React.useMemo(() => {
    if (activeTab !== 'awards') {
      return [];
    }

    const categories = new Set<string>();
    filteredAndSortedProducts.forEach((product) => {
      if (product.awards) {
        product.awards.forEach((award) => {
          if (award.category) {
            categories.add(award.category);
          }
        });
      }
    });

    return Array.from(categories).sort();
  }, [filteredAndSortedProducts, activeTab]);

  // Handle tab change with caching
  const handleTabChange = useCallback(
    async (tab: NavigationTab) => {
      try {
        // Only set loading when we're actually going to fetch data or clear results
        // Don't show loading when clicking search tab again (staying on search tab)
        const isStayingOnSearchTab = tab === 'search' && activeTab === 'search';

        // Don't show loading for tabs that won't auto-fetch data
        const willFetchData = tab === 'newest' || tab === 'awards' || tab === 'category' || tab === 'brands';

        if (!isStayingOnSearchTab && willFetchData) {
          setTabLoading(tab, true);
        }

        // Cache check (5 minutes)
        const CACHE_EXPIRY = 5 * 60 * 1000;
        const now = Date.now();

        // Pre-fetch required data before changing tab
        const promises: Array<Promise<any>> = [];

        // Fetch brands if needed for brands tab
        if (tab === 'brands' && (!cache.lastFetched.brands || now - cache.lastFetched.brands > CACHE_EXPIRY)) {
          promises.push(fetchBrands());
        }

        // Fetch categories if needed for category tab
        if (
          tab === 'category' &&
          (!cache.lastFetched.categories || now - cache.lastFetched.categories > CACHE_EXPIRY)
        ) {
          promises.push(fetchCategories());
        }

        // Wait for any required data to be fetched
        if (promises.length > 0) {
          await Promise.all(promises);
        }

        // Change tab and trigger products fetch
        startTransition(() => {
          // Simply switch to the new tab - each tab preserves its own data
          setActiveTab(tab);

          // Clear loading state if staying on search tab (since we didn't set it)
          if (isStayingOnSearchTab) {
            setTabLoading(tab, false);
          }

          // Clear loading state for tabs that don't auto-fetch data
          if (!willFetchData) {
            setTabLoading(tab, false);
          }
        });
      } catch (error) {
        console.error(`❌ [${hookInstanceId.current}] Error in handleTabChange:`, error);
        setTabLoading(tab, false); // Clear loading state on error
      }
    },
    [cache.lastFetched, fetchBrands, fetchCategories, activeTab, setTabLoading],
  );

  // Handle category change (also used for brand selection in brands tab)
  const handleCategoryChange = useCallback(
    (category: string) => {
      if (activeTab === 'brands') {
        setSelectedBrand(category);
      } else {
        setSelectedCategory(category);
        setSelectedSubcategory(''); // Reset subcategory when category changes
      }
    },
    [activeTab],
  );

  // Initialize lastUpdatedInfo on first page load
  useEffect(() => {
    fetchLastUpdatedInfo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty dependency array - only run once on mount

  // Fetch products when dependencies change (but after initialization)
  useEffect(() => {
    // Don't automatically fetch products for search tab - it should start empty
    if (activeTab === 'search') {
      return;
    }

    // For 'newest' tab, wait for lastUpdatedInfo to be loaded before fetching
    if (activeTab === 'newest' && !lastUpdatedInfo) {
      console.info(`⏳ [${hookInstanceId.current}] Waiting for lastUpdatedInfo before fetching newest products`);
      return;
    }

    // Always fetch for category and brands tabs (show all when no selection)

    // Call fetchProducts directly to avoid dependency issues
    fetchProducts(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    activeTab,
    selectedCategory,
    selectedSubcategory,
    selectedBrand,
    selectedAwardPeriod,
    queryLimit,
    lastUpdatedInfo,
  ]);

  // Get subcategories for selected category
  const subcategories = React.useMemo(() => {
    if (!selectedCategory || !cache.subcategories[selectedCategory]) {
      return [];
    }
    return cache.subcategories[selectedCategory];
  }, [selectedCategory, cache.subcategories]);

  // Get product by ID
  const getProductById = useCallback(
    (productId: string): GlowpickProduct | undefined => {
      return products.find((product) => product.productId === productId);
    },
    [products],
  );

  // Fetch full product details from API server (for ingredients and description)
  const fetchProductDetails = useCallback(async (productId: string): Promise<GlowpickProduct | null> => {
    try {
      const fullProduct = await glowpickClient.getProductById(productId);
      return fullProduct;
    } catch (error) {
      console.error(`❌ [${hookInstanceId.current}] Error fetching product details for ${productId}:`, error);
      return null;
    }
  }, []);

  // Handler for page changes (client-side pagination)
  const handlePageChange = useCallback((page: number) => {
    setCurrentPage(page);
    // Update pagination info for the new page
    setPaginationInfo((prev) => ({
      ...prev,
      currentPage: page,
      hasNextPage: page < prev.totalPages,
      hasPreviousPage: page > 1,
      startIndex: (page - 1) * prev.productsPerPage + 1,
      endIndex: Math.min(page * prev.productsPerPage, prev.totalProducts),
    }));
  }, []);

  // Handler for page size changes
  const handlePageSizeChange = useCallback(
    (newPageSize: number) => {
      setQueryLimit(newPageSize);
      setCurrentPage(1); // Reset to first page when changing page size

      // Immediately update pagination info with new page size
      setPaginationInfo((prev) => {
        const newInfo = calculatePaginationInfo(prev.totalProducts, 1, newPageSize);
        return newInfo;
      });
    },
    [calculatePaginationInfo],
  );

  // Client-side pagination: slice products based on current page
  const paginatedProducts = React.useMemo(() => {
    const startIndex = (currentPage - 1) * queryLimit;
    const endIndex = startIndex + queryLimit;
    return filteredAndSortedProducts.slice(startIndex, endIndex);
  }, [filteredAndSortedProducts, currentPage, queryLimit]);

  // Silent refresh method for updating data without loading indicators
  const silentRefresh = useCallback(async () => {
    await fetchProducts(currentPage, true);
  }, [fetchProducts, currentPage]);

  // Clear product metadata (for re-scraping)
  const clearProductMetadata = useCallback(
    async (productId: string): Promise<{ success: boolean; message?: string }> => {
      try {
        const result = await glowpickClient.clearProductMetadata(productId);
        if (result.success) {
          // Trigger silent refresh to update the UI
          await silentRefresh();
        }
        return result;
      } catch (error) {
        console.error(`❌ [${hookInstanceId.current}] Error clearing metadata for product ${productId}:`, error);
        return { success: false, message: error instanceof Error ? error.message : 'Unknown error' };
      }
    },
    [silentRefresh],
  );

  // Set products list to a single product (for search by ID)
  const setSearchProduct = useCallback(
    (product: GlowpickProduct) => {
      console.info(`🔍 [${hookInstanceId.current}] Setting search results to single product: ${product.productId}`);

      setTabProducts(activeTab, [product]);

      // Update pagination info for single product
      const newPaginationInfo = calculatePaginationInfo(1, 1, queryLimit);
      setPaginationInfo(newPaginationInfo);
      setCurrentPage(1);

      // Clear loading state to show the search results
      setTabLoading(activeTab, false);
    },
    [calculatePaginationInfo, queryLimit, setTabProducts, setTabLoading, activeTab],
  );

  return {
    // Products and data
    glowpickProducts: paginatedProducts,
    allProducts: products,
    brands: cache.brands,
    categories: cache.categories,
    subcategories,
    awardCategories,
    awardPeriods,

    // Pagination info and handlers
    paginationInfo,
    handlePageChange,
    handlePageSizeChange,

    // Navigation state
    activeTab,
    selectedCategory,
    selectedSubcategory,
    selectedBrand,
    selectedRankingType,
    selectedAwardPeriod,
    sortBy,
    queryLimit,

    // Loading states
    loading,

    // Actions
    handleTabChange,
    handleCategoryChange,
    setSelectedSubcategory,
    setSelectedBrand,
    setSelectedRankingType,
    setSelectedAwardPeriod,
    setSortBy,
    setQueryLimit,
    getProductById,
    fetchProductDetails,
    silentRefresh,
    clearProductMetadata,
    setSearchProduct,

    // Last updated info
    lastUpdatedInfo,
    fetchLastUpdatedInfo,
  };
};
